<template>
    <div class="qr-code">
        <div class="qr-code__instructions">
            <p class="qr-code__instructions-title">
                {{ $t('Following the steps below:') }}
            </p> 
            <p class="qr-code__instructions-text">
                {{ $t('1. Download and install Google Authenticator or any other authenticator app on your phone.') }}
            </p>
            <p class="qr-code__instructions-text">
                {{ $t('2. Open the app, scan the QR code below with your camera and enter the code it generates.') }}
            </p>
            <div class="qr-code__instructions-code">
                <img v-if="qrCode.url" :src="qrCode.url" alt="QR Code" :class="{'disabled': invalidCode}" />
                <div>
                    <p class="qr-code__instructions-code-question" :class="{'disabled': invalidCode}"> 
                        {{ $t('If you can’t scan QR code, you can manually enter the code below.') }} 
                    </p>
                    <div class="qr-code__instructions-code-content">
                        <div class="qr-code__code-input" :class="{'disabled': invalidCode}">
                            <span class="qr-code__code-secret">{{ qrCode.secret }}</span>
                            <button 
                                class="qr-code__copy-button" 
                                @click="copy(qrCode.secret)" 
                                :disabled="disabled || invalidCode"
                                type="button"
                            >
                                <span class="fas fa-copy"></span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <p class="qr-code__instructions-text">
                {{ $t('3. Enter the 6-digit code you see in your authenticator app.') }}
            </p>
            <verification-code ref="verificationCode" :length="6" @input="handleCodeInput" :disabled="invalidCode"/>
            <p :class="['qr-code__instructions-timer', { 'text-danger': invalidCode}]">
                {{ $t('Validated Code') }} {{ timeRemaining }}
            </p>
        </div>
        <div>
            <ui-button @click="tryAgain" :loading="loading" :disabled="!invalidCode" class="w-50">
                {{ $t('Try again') }}
                <span class="fas fa-sync"></span>
            </ui-button>
        </div>
        <div>
            <b-button @click="dontShowAgain" :loading="cancel" :disabled="cancel" variant="link" type="button" class="qr-code__dont-show-again">
                <span v-if="loading" class="ui-button__loader fas fa-spinner fa-spin"></span>
                {{ $t('Dont show again.') }}
            </b-button>
        </div>
        <vue-recaptcha
        class="w-100"
        ref="recaptcha"
        size="invisible"
        @verify="validateCode"
        :sitekey="CONST.CAPTCHACODE"
        :loadRecaptchaScript="true"
        />
    </div>
</template>

<script>
import QR from 'qrcode';
import VueRecaptcha from 'vue-recaptcha';
import recaptchaUtils from '@/utils/recaptcha.utils';

import UiButton from '@/components/ui/buttons/Button.vue';
import VerificationCode from '@/components/Code.vue';

export default {
    name: 'QrCode',
    components: { VueRecaptcha, UiButton, VerificationCode },
    data() {
        return {
            loading: false,
            disabled: false,
            formData: {
                otp_url: '',
                token: ''
            },
            qrCode: {
                secret: '',
                url: ''
            },
            secondsRemaining: 0,
            timeRemaining: null,
            timeInterval: null,
            validatingCode: false,
            verificationCode: '',
            showVerificationSuccess: false,
            invalidCode: false,
            cancel: false,
        };
    },
    mounted() {
        this.createQrCode();
        this.setTimeRemaining(300);
    },
    methods: {
        async createQrCode() {
            try {
                const { data } = await this.axiosAccount.get('/api/two-factor/generate');
                const { otp_url } = data;
                this.qrCode.url = await QR.toDataURL(otp_url);
                this.qrCode.secret = new URL(data.otp_url).searchParams.get('secret');
                this.formData.otp_url = otp_url;
            } catch(error) {
                this.$toasted.global.errorMessage(error);
            }
        },
        copy(value) {
            navigator.clipboard.writeText(value);
            this.$toasted.global.successMessage(this.$t('Copied to clipboard.'));
        },
        setTimeRemaining(expiration_seconds) {
            this.secondsRemaining = expiration_seconds;
            this.timeInterval = setInterval(() => {
                if (this.secondsRemaining > 0) {
                    this.secondsRemaining--;
                    this.timeRemaining = this.$moment().startOf('day').seconds(this.secondsRemaining).format('mm:ss')
                } else {
                    clearInterval(this.timeInterval);
                    this.invalidCode = true;
                }
            }, 1000);
        },
        async redirectTo() {
            const { redirect_url } = this.$route.query;
            if (redirect_url) {
                window.location.replace(redirect_url);
                return;
            }
            this.$router.push({ name: 'dashboard'})
        },
        handleCodeInput(code) {
            this.formData.token = code;
            if (code.length === 6) {
                this.handleSubmit();
            }
        },
        async tryAgain() {
            this.validatingCode = false;
            this.invalidCode = false;
            this.$refs.verificationCode.reset();
            this.createQrCode();
            this.setTimeRemaining(200);            
        },
        async handleSubmit() {
            if (!recaptchaUtils.isAvailable()) {
                this.validateCode();
                return;
            }

            this.$refs.recaptcha.reset();
            this.$refs.recaptcha.execute();
        },
        async validateCode() {
            try {
                this.validatingCode = true;
                
                await this.axiosAccount.post('/api/two-factor/verify', this.formData);
                this.$emit('success');
            } catch (error) {
                this.showError(error);
                if (error.response.status !== 400) {
                    this.invalidCode = true;
                }
            } finally {
                this.$refs.verificationCode.reset();
                this.validatingCode = false;
            }
        },
        async dontShowAgain() {
            try {
                this.cancel = true;
                await this.axiosAccount.put('/api/accounts/preferences', { no_display_tfa: true });
                setTimeout(() => {
                    this.redirectTo();
                }, 3000);
            } catch (error) {
                this.showError(error);
            } finally {
                this.cancel = false;
            }
        },
    },
};
</script>

<style lang="scss">
.qr-code {

    &__instructions {
        padding: 16px;

        @media (max-width: 1000px) {
            padding: 16px;
        }
    }

    &__instructions-title {
        font-weight: 500;
        font-size: 18px;
        line-height: 21.94px;
        color: #404042;
        text-align: left;

        @media (max-width: 1000px) {
            margin: 16px;
        }
    }

    &__instructions-text {
        font-size: 16px;
        line-height: 19.5px;
        font-weight: 400;
        color: #404042;
        text-align: left;

        @media (max-width: 1000px) {
            margin: 16px;
        }
    }

    &__instructions-code {
        display: flex;
        align-items: center;
        margin:  16px;

        img {
            width: 139.53px;
            height: 140px;
            margin: 32px;
        }

        img.disabled {
            opacity: 0.5;
            pointer-events: none;
        }

        @media (max-width: 1000px) {
            flex-direction: column;
            align-items: center;
            margin: 16px;
        }
    }

    &__instructions-code-question {
        font-size: 16px;
        line-height: 19.5px;
        font-weight: 500;
        color: #404042;
        text-align: left;
        transition: opacity 0.3s ease;

        @media (max-width: 1000px) {
            flex-direction: column;
            align-items: center;
            text-align: center;
        }
    }

    &__instructions-code-content {
        display: flex;
        align-items: center;
        margin-top: 12px;

        @media (max-width: 1000px) {
            flex-direction: column;
            align-items: center;
            margin: 8px;
        }
    }

    &__code-input {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        max-width: 400px;
        background-color: #f5f3f7;
        border-radius: 8px;
        padding: 12px 16px;
        position: relative;
        transition: opacity 0.3s ease;

        @media (max-width: 1000px) {
            max-width: 100%;
        }
    }

    &__code-secret {
        font-size: 16px;
        line-height: 19.5px;
        font-weight: 500;
        color: #404042;
        text-align: center;
        flex: 1;
        letter-spacing: 1px;
        font-family: monospace;
    }

    &__copy-button {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #404042;
        transition: opacity 0.2s ease;

        &:hover:not(:disabled) {
            opacity: 0.7;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }

        .fas {
            font-size: 16px;
        }
    }

    &__instructions-code-secret {
        font-size: 14px;
        line-height: 17.07px;
        font-weight: 400;
        color: #404042;
        text-align: left;
        transition: opacity 0.3s ease;

        @media (max-width: 1000px) {
            flex-direction: column;
            align-items: center;
        }
    }

    &__instructions-timer {
        font-size: 14px;
        color: var(--color-accent-base);
        font-weight: 400;
        margin: 0 !important;
        line-height: 17.07px;
    }

    .disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    &__no-show-again {
        color: $accounts-secondary !important;
        font-weight: 500 !important;
        padding: 10px !important;
        text-align: center;
    }
}
</style>